import * as React from "react";
import { Loader2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { DDQAnsweredQuestion, DDQParsedQuestions } from "@/lib/api";
import { DDQAccordionRenderer } from "./AccordionRenderer";

/**
 * Live progress view shown while a DDQ is being answered. The completed
 * Q&As are rendered through the same AccordionRenderer used for the
 * persisted final message, so what the LP sees during streaming matches
 * the eventual stored representation.
 */
export function DDQProgressView({
  displayName,
  fundName,
  total,
  answered,
  results,
  retrievalDone,
  onCancel,
  cancelling,
  parsedStructure,
}: {
  displayName: string;
  fundName: string;
  total: number;
  answered: number;
  results: DDQAnsweredQuestion[];
  retrievalDone: boolean;
  onCancel: () => void;
  cancelling: boolean;
  parsedStructure?: DDQParsedQuestions | null;
}) {
  const percent = total > 0 ? Math.min(100, Math.round((answered / total) * 100)) : 0;
  const needsFollowup = results.filter((r) => r.needs_gp_followup).length;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-4">
      <div className="rounded-md border bg-card p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Loader2 className="size-4 animate-spin text-brand" />
              <div className="text-sm font-semibold">DDQ run in progress</div>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{displayName}</span>{" "}
              · {fundName} · {answered} of {total} answered
              {needsFollowup > 0 ? ` · ${needsFollowup} need GP follow-up` : ""}
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={cancelling}
          >
            <XCircle />
            {cancelling ? "Cancelling…" : "Cancel run"}
          </Button>
        </div>

        <div
          className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={answered}
        >
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge variant={retrievalDone ? "secondary" : "outline"}>
            {retrievalDone ? "Retrieval complete" : "Retrieving…"}
          </Badge>
          <Badge variant={answered > 0 ? "secondary" : "outline"}>
            Generating answers ({percent}%)
          </Badge>
        </div>

        {parsedStructure?.sections?.length ? (
          <SectionProgressStrips
            sections={parsedStructure.sections}
            results={results}
          />
        ) : null}
      </div>

      {results.length > 0 ? (
        <DDQAccordionRenderer
          questions={results}
          parsedStructure={parsedStructure ?? null}
          showTransient={answered < total}
        />
      ) : (
        <div className="rounded-md border border-dashed bg-card p-8 text-center text-xs text-muted-foreground">
          Sourcing chunks from the {fundName} data room… answers will stream in here.
        </div>
      )}
    </div>
  );
}

function SectionProgressStrips({
  sections,
  results,
}: {
  sections: DDQParsedQuestions["sections"];
  results: DDQAnsweredQuestion[];
}) {
  // Build a lookup of section → set(question_id) to count answered Qs per
  // section without scanning the whole results array per section.
  const sectionTotals = React.useMemo(() => {
    const out: { name: string; total: number; ids: Set<string> }[] = [];
    for (const sec of sections) {
      const ids = new Set<string>();
      for (const q of sec.questions) ids.add(q.id);
      out.push({ name: sec.name, total: sec.questions.length, ids });
    }
    return out;
  }, [sections]);

  const answeredBySection = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of sectionTotals) counts.set(s.name, 0);
    for (const r of results) {
      for (const s of sectionTotals) {
        if (s.ids.has(r.question_id)) {
          counts.set(s.name, (counts.get(s.name) || 0) + 1);
          break;
        }
      }
    }
    return counts;
  }, [results, sectionTotals]);

  return (
    <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
      {sectionTotals.map((s) => {
        const done = answeredBySection.get(s.name) || 0;
        const pct = s.total > 0 ? Math.round((done / s.total) * 100) : 0;
        const complete = done >= s.total;
        return (
          <div
            key={s.name}
            className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-[11px]"
          >
            <div
              className="h-1.5 w-12 shrink-0 overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={s.total}
              aria-valuenow={done}
              aria-label={`${s.name} progress`}
            >
              <div
                className={complete ? "h-full bg-success" : "h-full bg-primary transition-all"}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="min-w-0 flex-1 truncate font-medium text-foreground">
              {s.name}
            </span>
            <span className="shrink-0 font-mono text-muted-foreground">
              {done}/{s.total}
            </span>
          </div>
        );
      })}
    </div>
  );
}
